import { useState, useRef, useEffect, useLayoutEffect } from 'react'
import { Rnd } from 'react-rnd'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'


// Stałe rozmiaru A4 w px
const A4_WIDTH = 794
const A4_HEIGHT = 1123
const STORAGE_KEY = 'moja-strona2-draft'

const DEFAULT_FORM = {
  email: '',
  nadawca: { imie: '', miasto: '', kod: '', woj: '', adres: '' },
  odbiorca: { imie: '', miasto: '', kod: '', woj: '', adres: '' },
  acceptTerms: false
}

function loadStored() {
  if (typeof window === 'undefined') {
    return { form: { ...DEFAULT_FORM, nadawca: { ...DEFAULT_FORM.nadawca }, odbiorca: { ...DEFAULT_FORM.odbiorca } }, elements: [] }
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      return { form: { ...DEFAULT_FORM, nadawca: { ...DEFAULT_FORM.nadawca }, odbiorca: { ...DEFAULT_FORM.odbiorca } }, elements: [] }
    }
    const data = JSON.parse(raw)
    const form = {
      ...DEFAULT_FORM,
      ...data.form,
      nadawca: { ...DEFAULT_FORM.nadawca, ...data.form?.nadawca },
      odbiorca: { ...DEFAULT_FORM.odbiorca, ...data.form?.odbiorca }
    }
    return { form, elements: Array.isArray(data.elements) ? data.elements.map(normalizeElement) : [] }
  } catch {
    return { form: { ...DEFAULT_FORM, nadawca: { ...DEFAULT_FORM.nadawca }, odbiorca: { ...DEFAULT_FORM.odbiorca } }, elements: [] }
  }
}

async function blobUrlToDataUrl(src) {
  const res = await fetch(src)
  const blob = await res.blob()
  return new Promise((resolve, reject) => {
    const fr = new FileReader()
    fr.onload = () => resolve(fr.result)
    fr.onerror = reject
    fr.readAsDataURL(blob)
  })
}

async function prepareElementsForStorage(elements) {
  const next = []
  for (const el of elements) {
    if (el.type === 'image' && typeof el.src === 'string' && el.src.startsWith('blob:')) {
      try {
        const dataUrl = await blobUrlToDataUrl(el.src)
        next.push({ ...el, src: dataUrl })
      } catch {
        next.push(el)
      }
    } else {
      next.push(el)
    }
  }
  return next
}

function saveDraft(form, elements) {
  return prepareElementsForStorage(elements)
    .then((els) => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ form, elements: els }))
    })
    .catch(() => {})
}

const MAX_UNDO_HISTORY = 50

const DEFAULT_TEXT_STYLE = {
  fontWeight: 'normal',
  fontStyle: 'normal',
  textDecoration: 'none',
  textAlign: 'left',
  lineHeight: 1.2,
  letterSpacing: 0
}

const FONT_OPTIONS = [
  'Arial',
  'Verdana',
  'Times New Roman',
  'Courier New',
  'Georgia',
  'Impact',
  'Comic Sans MS'
]

function getTextTypographyStyles(el, zoom = 1) {
  return {
    fontWeight: el.fontWeight ?? DEFAULT_TEXT_STYLE.fontWeight,
    fontStyle: el.fontStyle ?? DEFAULT_TEXT_STYLE.fontStyle,
    textDecoration: el.textDecoration ?? DEFAULT_TEXT_STYLE.textDecoration,
    textAlign: el.textAlign ?? DEFAULT_TEXT_STYLE.textAlign,
    lineHeight: el.lineHeight ?? DEFAULT_TEXT_STYLE.lineHeight,
    letterSpacing: `${(el.letterSpacing ?? DEFAULT_TEXT_STYLE.letterSpacing) * zoom}px`
  }
}

function normalizeElement(el) {
  return { ...el, locked: el.locked ?? false }
}

function cloneElements(els) {
  return els.map((e) => ({ ...e }))
}

function detectTouchDevice() {
  if (typeof window === 'undefined') return false
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0
}

export default function App() {
  const fileRef = useRef(null)
  const hiddenRef = useRef(null)
  const hiddenPage2Ref = useRef(null)
  const canvasRef = useRef(null)
  const wrapperRef = useRef(null)
  const start = useRef({ x: 0, y: 0 })
  const init = useRef(null)
  const initialSnapshot = useRef(null)
  if (initialSnapshot.current === null) {
    initialSnapshot.current = loadStored()
  }

  const [page, setPage] = useState('landing')
  const [modal, setModal] = useState(null) // 'contact' | 'terms' | null
  const [paymentSuccess, setPaymentSuccess] = useState(false)
  const [paymentCanceled, setPaymentCanceled] = useState(false)
  const [form, setForm] = useState(() => initialSnapshot.current.form)
  const [elements, setElements] = useState(() => initialSnapshot.current.elements.map(normalizeElement))
  const [selectedId, setSelectedId] = useState(null)
  const zIndexCounter = useRef(1)
  const [isTouchDevice] = useState(() => detectTouchDevice())
  const [isNarrowViewport, setIsNarrowViewport] = useState(() => typeof window !== 'undefined' && window.innerWidth <= 768)
  const isMobile = isTouchDevice || isNarrowViewport
  const [zoom, setZoom] = useState(() => {
    const narrow = typeof window !== 'undefined' && window.innerWidth <= 768
    return detectTouchDevice() || narrow ? 0.4 : 1
  })
  const [showTextPanel, setShowTextPanel] = useState(false)
  const [isGenerating, setIsGenerating] = useState(false)
  const [progress, setProgress] = useState(0)

  const pastRef = useRef([])
  const futureRef = useRef([])
  const elementsRef = useRef(elements)
  elementsRef.current = elements
  const selectedIdRef = useRef(selectedId)
  selectedIdRef.current = selectedId
  const textHistoryTimerRef = useRef(null)
  const textHistoryPrimedRef = useRef(false)
  const [, setHistoryUi] = useState(0)
  const bumpHistoryUi = () => setHistoryUi((n) => n + 1)

  const syncZIndexFromElements = (els) => {
    if (els.length) zIndexCounter.current = Math.max(1, ...els.map((e) => e.zIndex || 0))
  }

  const pushUndoSnapshot = () => {
    pastRef.current.push(cloneElements(elementsRef.current))
    if (pastRef.current.length > MAX_UNDO_HISTORY) pastRef.current.shift()
    futureRef.current = []
    bumpHistoryUi()
  }

  const undo = () => {
    if (pastRef.current.length === 0) return
    const prev = pastRef.current.pop()
    futureRef.current.push(cloneElements(elementsRef.current))
    setElements(prev)
    syncZIndexFromElements(prev)
    bumpHistoryUi()
  }

  const redo = () => {
    if (futureRef.current.length === 0) return
    const next = futureRef.current.pop()
    pastRef.current.push(cloneElements(elementsRef.current))
    setElements(next)
    syncZIndexFromElements(next)
    bumpHistoryUi()
  }

  const toggleLock = (id) => {
    pushUndoSnapshot()
    setElements((prev) => prev.map((el) => (el.id === id ? { ...el, locked: !(el.locked ?? false) } : el)))
  }

  const bringToFront = (id) => {
    pushUndoSnapshot()
    setElements((prev) => {
      const maxZ = Math.max(...prev.map((el) => el.zIndex || 0))
      const next = prev.map((el) => (el.id === id ? { ...el, zIndex: maxZ + 1 } : el))
      syncZIndexFromElements(next)
      return next
    })
  }

  const sendToBack = (id) => {
    pushUndoSnapshot()
    setElements((prev) => {
      const minZ = Math.min(...prev.map((el) => el.zIndex || 0))
      return prev.map((el) => {
        if (el.id === id) {
          return { ...el, zIndex: Math.max(0, minZ - 1) }
        }
        return { ...el, zIndex: Math.max(0, el.zIndex || 0) }
      })
    })
  }

  const bringForward = (id) => {
    pushUndoSnapshot()
    setElements((prev) => {
      const current = prev.find((el) => el.id === id)
      if (!current) return prev
      const higher = prev
        .filter((el) => el.id !== id && (el.zIndex || 0) > (current.zIndex || 0))
        .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))[0]
      if (!higher) return prev
      return prev.map((el) => {
        if (el.id === id) return { ...el, zIndex: higher.zIndex }
        if (el.id === higher.id) return { ...el, zIndex: current.zIndex }
        return el
      })
    })
  }

  const sendBackward = (id) => {
    pushUndoSnapshot()
    setElements((prev) => {
      const current = prev.find((el) => el.id === id)
      if (!current) return prev
      const lower = prev
        .filter((el) => el.id !== id && (el.zIndex || 0) < (current.zIndex || 0))
        .sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0))[0]
      if (!lower) return prev
      return prev.map((el) => {
        if (el.id === id) return { ...el, zIndex: lower.zIndex }
        if (el.id === lower.id) return { ...el, zIndex: current.zIndex }
        return el
      })
    })
  }

  const undoRef = useRef(undo)
  const redoRef = useRef(redo)
  undoRef.current = undo
  redoRef.current = redo

  useLayoutEffect(() => {
    const els = initialSnapshot.current.elements
    if (els.length) {
      zIndexCounter.current = Math.max(1, ...els.map((e) => e.zIndex || 0))
    }
  }, [])

  useEffect(() => {
    const t = setTimeout(() => {
      void saveDraft(form, elements)
    }, 500)
    return () => clearTimeout(t)
  }, [form, elements])

  useEffect(() => {
    const handleResize = () => {
      const narrow = window.innerWidth <= 768
      setIsNarrowViewport(narrow)
      setZoom((detectTouchDevice() || narrow) ? 0.4 : 1)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (page !== 'editor') return undefined
    const onKeyDown = (e) => {
      const mod = e.ctrlKey || e.metaKey
      if (!mod || e.key.toLowerCase() !== 'z') return
      const t = e.target
      const tag = t?.tagName
      if (tag === 'TEXTAREA' || tag === 'INPUT' || tag === 'SELECT' || t?.isContentEditable) return
      e.preventDefault()
      if (e.shiftKey) redoRef.current()
      else undoRef.current()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [page])

  useEffect(() => {
    if (selectedId != null && !elements.some((el) => el.id === selectedId)) setSelectedId(null)
  }, [elements, selectedId])

  useEffect(() => {
    if (!isGenerating) return

    let value = 0
    const interval = setInterval(() => {
      value += Math.random() * 10
      if (value >= 95) value = 95
      setProgress(Math.floor(value))
    }, 300)

    return () => clearInterval(interval)
  }, [isGenerating])

  useEffect(() => {
    if (page !== 'editor') return undefined
    const onPointerDown = (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return
      const sid = selectedIdRef.current
      if (sid == null) return
      const t = e.target
      if (!(t instanceof Element)) return
      const host = t.closest('[data-editor-element]')
      const panel = t.closest('[data-editor-panel]')
      const isUI =
        t.closest('button') ||
        t.closest('input') ||
        t.closest('select') ||
        t.closest('textarea') ||
        t.closest('[role="button"]')
      if (host || panel || isUI) return
      setSelectedId(null)
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    return () => document.removeEventListener('pointerdown', onPointerDown, true)
  }, [page])

  // Po powrocie ze Stripe Checkout: pokazujemy odpowiedni ekran i czyścimy query z paska adresu.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const paymentStatus = params.get('payment')
    if (paymentStatus !== 'success' && paymentStatus !== 'cancel') return
    if (paymentStatus === 'success') {
      setPaymentSuccess(true)
      setPaymentCanceled(false)
    } else {
      setPaymentCanceled(true)
      setPaymentSuccess(false)
    }

    params.delete('session_id')
    params.delete('payment')
    params.delete('orderId')
    const qs = params.toString()
    const nextUrl = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`
    window.history.replaceState({}, '', nextUrl)
  }, [])

  const addImage = (e) => {
    const file = e.target.files[0]
    if (!file) return
    const url = URL.createObjectURL(file)
    zIndexCounter.current += 1
    pushUndoSnapshot()
    setElements(prev => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        type: 'image',
        x: 100,
        y: 100,
        width: 200,
        height: 200,
        rotate: 0,
        src: url,
        locked: false,
        zIndex: zIndexCounter.current
      }
    ])
    e.target.value = null
  }

  const addText = () => {
    zIndexCounter.current += 1
    pushUndoSnapshot()
    setElements(prev => [
      ...prev,
      {
        id: Date.now(),
        type: 'text',
        x: 120,
        y: 120,
        width: 220,
        height: 120,
        text: 'Text',
        fontSize: 18,
        color: '#000',
        fontFamily: 'Arial',
        rotate: 0,
        fontWeight: DEFAULT_TEXT_STYLE.fontWeight,
        fontStyle: DEFAULT_TEXT_STYLE.fontStyle,
        textDecoration: DEFAULT_TEXT_STYLE.textDecoration,
        textAlign: DEFAULT_TEXT_STYLE.textAlign,
        lineHeight: DEFAULT_TEXT_STYLE.lineHeight,
        letterSpacing: DEFAULT_TEXT_STYLE.letterSpacing,
        locked: false,
        zIndex: zIndexCounter.current
      }
    ])
  }

  const update = (id, data) => {
    if (Object.prototype.hasOwnProperty.call(data, 'text')) {
      if (!textHistoryPrimedRef.current) {
        pushUndoSnapshot()
        textHistoryPrimedRef.current = true
      }
      clearTimeout(textHistoryTimerRef.current)
      textHistoryTimerRef.current = setTimeout(() => {
        textHistoryPrimedRef.current = false
      }, 450)
    } else {
      clearTimeout(textHistoryTimerRef.current)
      textHistoryPrimedRef.current = false
      pushUndoSnapshot()
    }
    setElements(prev =>
      prev.map(el => (el.id === id ? { ...el, ...data } : el))
    )
  }

  const deleteEl = (id) => {
    pushUndoSnapshot()
    setElements(prev => prev.filter(el => el.id !== id))
    setSelectedId(null)
  }

  const startMove = (e, el) => {
    if (el.locked) return
    e.stopPropagation()
    if (typeof e.preventDefault === 'function') e.preventDefault()
    if (e.pointerId && e.currentTarget?.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId)
    }

    setSelectedId(el.id)
    pushUndoSnapshot()
    const pointX = e.touches?.[0]?.clientX ?? e.clientX
    const pointY = e.touches?.[0]?.clientY ?? e.clientY
    const wrapperRect = wrapperRef.current?.getBoundingClientRect()
    
    start.current = { 
      x: pointX, 
      y: pointY,
      rectLeft: wrapperRect?.left || 0,
      rectTop: wrapperRect?.top || 0,
      scrollLeft: wrapperRef.current?.scrollLeft || 0,
      scrollTop: wrapperRef.current?.scrollTop || 0
    }
    init.current = { ...el }

    const move = (moveEvent) => {
      if (typeof moveEvent.preventDefault === 'function') moveEvent.preventDefault()
      const curX = moveEvent.touches?.[0]?.clientX ?? moveEvent.clientX
      const curY = moveEvent.touches?.[0]?.clientY ?? moveEvent.clientY
      
      const canvasRect = canvasRef.current?.getBoundingClientRect()
      const scaleX = A4_WIDTH / (canvasRect?.width || A4_WIDTH)
      const scaleY = A4_HEIGHT / (canvasRect?.height || A4_HEIGHT)
      const canvasLeft = canvasRect?.left || 0
      const canvasTop = canvasRect?.top || 0
      
      const startCanvasX = (start.current.x - canvasLeft) * scaleX
      const startCanvasY = (start.current.y - canvasTop) * scaleY
      const curCanvasX = (curX - canvasLeft) * scaleX
      const curCanvasY = (curY - canvasTop) * scaleY
      
      const canvasDeltaX = Math.round(curCanvasX - startCanvasX)
      const canvasDeltaY = Math.round(curCanvasY - startCanvasY)

      const maxX = A4_WIDTH - init.current.width
      const maxY = A4_HEIGHT - init.current.height
      const newX = Math.max(0, Math.min(init.current.x + canvasDeltaX, maxX))
      const newY = Math.max(0, Math.min(init.current.y + canvasDeltaY, maxY))

      setElements(prev => prev.map(i => (i.id === el.id ? { ...i, x: newX, y: newY } : i)))
    }

    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
      window.removeEventListener('touchmove', move)
      window.removeEventListener('touchend', up)
    }

    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    window.addEventListener('touchmove', move, { passive: false })
    window.addEventListener('touchend', up)
  }

  const startResize = (e, el) => {
    if (el.locked) return
    e.stopPropagation()
    if (typeof e.preventDefault === 'function') e.preventDefault()
    if (e.pointerId && e.currentTarget?.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId)
    }

    setSelectedId(el.id)
    pushUndoSnapshot()
    const pointX = e.touches?.[0]?.clientX ?? e.clientX
    const pointY = e.touches?.[0]?.clientY ?? e.clientY
    const wrapperRect = wrapperRef.current?.getBoundingClientRect()
    
    start.current = { 
      x: pointX, 
      y: pointY,
      rectLeft: wrapperRect?.left || 0,
      rectTop: wrapperRect?.top || 0,
      scrollLeft: wrapperRef.current?.scrollLeft || 0,
      scrollTop: wrapperRef.current?.scrollTop || 0
    }
    init.current = { ...el }

    const move = (moveEvent) => {
      if (typeof moveEvent.preventDefault === 'function') moveEvent.preventDefault()
      const curX = moveEvent.touches?.[0]?.clientX ?? moveEvent.clientX
      const curY = moveEvent.touches?.[0]?.clientY ?? moveEvent.clientY
      
      const canvasRect = canvasRef.current?.getBoundingClientRect()
      const scaleX = A4_WIDTH / (canvasRect?.width || A4_WIDTH)
      const scaleY = A4_HEIGHT / (canvasRect?.height || A4_HEIGHT)
      const canvasLeft = canvasRect?.left || 0
      const canvasTop = canvasRect?.top || 0
      
      const startCanvasX = (start.current.x - canvasLeft) * scaleX
      const startCanvasY = (start.current.y - canvasTop) * scaleY
      const curCanvasX = (curX - canvasLeft) * scaleX
      const curCanvasY = (curY - canvasTop) * scaleY
      
      const canvasDeltaX = Math.round(curCanvasX - startCanvasX)
      const canvasDeltaY = Math.round(curCanvasY - startCanvasY)

      const maxWidth = A4_WIDTH - init.current.x
      const maxHeight = A4_HEIGHT - init.current.y
      const newWidth = Math.max(50, Math.min(init.current.width + canvasDeltaX, maxWidth))
      const newHeight = Math.max(50, Math.min(init.current.height + canvasDeltaY, maxHeight))

      setElements(prev => prev.map(i => (i.id === el.id ? { ...i, width: newWidth, height: newHeight } : i)))
    }

    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
      window.removeEventListener('touchmove', move)
      window.removeEventListener('touchend', up)
    }

    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    window.addEventListener('touchmove', move, { passive: false })
    window.addEventListener('touchend', up)
  }

  const startRotate = (e, el) => {
    if (el.locked) return
    e.stopPropagation()
    if (typeof e.preventDefault === 'function') e.preventDefault()
    if (e.pointerId && e.currentTarget?.setPointerCapture) {
      e.currentTarget.setPointerCapture(e.pointerId)
    }

    setSelectedId(el.id)
    pushUndoSnapshot()
    const startX = e.touches?.[0]?.clientX ?? e.clientX
    start.current = { x: startX }
    init.current = { ...el }

    const move = (moveEvent) => {
      if (typeof moveEvent.preventDefault === 'function') moveEvent.preventDefault()
      const curX = moveEvent.touches?.[0]?.clientX ?? moveEvent.clientX
      const vpDeltaX = curX - start.current.x
      
      const canvasRect = canvasRef.current?.getBoundingClientRect()
      const scaleX = A4_WIDTH / (canvasRect?.width || A4_WIDTH)
      const canvasDeltaX = Math.round(vpDeltaX * scaleX)
      const newRotate = init.current.rotate + canvasDeltaX * 0.5

      setElements(prev => prev.map(i => (i.id === el.id ? { ...i, rotate: newRotate } : i)))
    }

    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      window.removeEventListener('pointercancel', up)
      window.removeEventListener('touchmove', move)
      window.removeEventListener('touchend', up)
    }

    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
    window.addEventListener('pointercancel', up)
    window.addEventListener('touchmove', move, { passive: false })
    window.addEventListener('touchend', up)
  }

  const selected = elements.find(e => e.id === selectedId)

  const handleGenerateClick = () => {
    if (isGenerating) return
    setIsGenerating(true)
    setProgress(0)
    if (!form.email.trim()) {
      setIsGenerating(false)
      setProgress(0)
      alert('Podaj email')
      return
    }
    const receiverRequiredFields = ['imie', 'miasto', 'kod', 'woj', 'adres']
    const hasEmptyReceiverField = receiverRequiredFields.some((field) => !form.odbiorca[field]?.trim())
    if (hasEmptyReceiverField) {
      setIsGenerating(false)
      setProgress(0)
      alert('Uzupełnij dane odbiorcy')
      return
    }
    if (!form.acceptTerms) {
      setIsGenerating(false)
      setProgress(0)
      alert('Musisz zaakceptować regulamin')
      return
    }
    generatePDF()
  }

  const generatePDF = async () => {
    if (!hiddenRef.current || !hiddenPage2Ref.current) return

    try {
      const pdf = new jsPDF('p', 'pt', 'a4')

      // PAGE 1 (projekt)
      const canvas1 = await html2canvas(hiddenRef.current, { scale: 1 })
      const img1 = canvas1.toDataURL('image/png')
      const w = pdf.internal.pageSize.getWidth()
      const h = pdf.internal.pageSize.getHeight()
      pdf.addImage(img1, 'PNG', 0, 0, w, h)

      // PAGE 2 (dane)
      pdf.addPage()
      const canvas2 = await html2canvas(hiddenPage2Ref.current, { scale: 1 })
      const img2 = canvas2.toDataURL('image/png')
      pdf.addImage(img2, 'PNG', 0, 0, w, h)

      const pdfBlob = pdf.output('blob')

      const uploadFormData = new FormData()
      uploadFormData.append('file', pdfBlob, 'projekt.pdf')
      uploadFormData.append('email', form.email)

      const uploadRes = await fetch('https://moja-strona-1-zfwj.onrender.com/upload', {
        method: 'POST',
        body: uploadFormData
      })

      if (!uploadRes.ok) {
        const text = await uploadRes.text()
        alert("Upload error: " + text)
        return
      }

      const uploadData = await uploadRes.json()

      const stripeRes = await fetch('https://moja-strona-1-zfwj.onrender.com/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orderId: uploadData.orderId })
      })

      const stripeData = await stripeRes.json()

      window.location.href = stripeData.url
    } catch (err) {
      console.error(err)
      alert('Błąd PDF')
    }
  }

  const getStyles = (mobile) => {
    const safeT = 'env(safe-area-inset-top, 0px)'
    const safeB = 'env(safe-area-inset-bottom, 0px)'
    const safeL = 'env(safe-area-inset-left, 0px)'
    const safeR = 'env(safe-area-inset-right, 0px)'
    const mobileToolbarReserve = `calc(76px + ${safeB})`

    const baseStyles = {
      app: {
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        paddingBottom: mobile ? mobileToolbarReserve : 0
      },
      topbarOuter: {
        background: '#D7E9FF',
        paddingTop: mobile ? `calc(12px + ${safeT})` : 20,
        paddingBottom: mobile ? 12 : 20,
        paddingLeft: mobile ? `max(12px, ${safeL})` : 20,
        paddingRight: mobile ? `max(12px, ${safeR})` : 20
      },
      topbarInner: { background: '#EDF5FF', borderRadius: 16, padding: mobile ? '10px 15px' : '10px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: mobile ? 'wrap' : 'nowrap', gap: mobile ? 8 : 0, boxShadow: '0 4px 14px rgba(31,41,55,0.08)' },
      logo: { background: '#BFDBFE', color: '#1F2937', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '4px 10px', borderRadius: 12, fontWeight: 'bold', fontSize: mobile ? 14 : 16, boxShadow: '0 2px 6px rgba(0,0,0,0.05)', overflow: 'hidden' },
      menu: { display: 'flex', gap: mobile ? 15 : 30, alignItems: 'center' },
      menuItem: { color: '#1F2937', fontSize: mobile ? 14 : 18, cursor: 'pointer', opacity: 0.8, transition: 'all 0.2s ease' },
      activeMenuItem: { opacity: 1, fontWeight: 'bold' },
      nextButton: { background: '#f59e0b', border: 'none', padding: '10px 18px', borderRadius: 20, color: 'white', fontWeight: 'bold', fontSize: 14, cursor: 'pointer' },
      main: { flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' },
      sidebar: { width: 200, background: 'linear-gradient(180deg, #273549 0%, #1F2937 50%, #18202D 100%)', padding: 14, display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05), inset 0 -6px 14px rgba(0,0,0,0.2)' },
      sidebarBtn: { width: '100%', padding: '16px', background: '#F1F6FF', color: '#1F2937', borderRadius: 14, cursor: 'pointer', textAlign: 'left', fontSize: 15, fontWeight: 'bold', transition: 'all 0.2s ease', boxSizing: 'border-box', boxShadow: '0 4px 10px rgba(0,0,0,0.06)', display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #e2ebff' },
      sidebarBtnIcon: { width: 28, height: 28, borderRadius: 10, background: '#DBEAFE', color: '#2563EB', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 14, flexShrink: 0 },
      canvasWrapper: {
        width: '100%',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        position: 'relative',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain',
        touchAction: 'pan-x pan-y'
      },
      rightPanel: { width: 250, background: '#F6F9FC', padding: 15, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' },
      formOverlay: {
        minHeight: '100dvh',
        boxSizing: 'border-box',
        paddingTop: `calc(20px + ${safeT})`,
        paddingBottom: `calc(20px + ${safeB})`,
        paddingLeft: `max(20px, ${safeL})`,
        paddingRight: `max(20px, ${safeR})`,
        background: 'rgba(0,0,0,0.4)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'auto',
        touchAction: 'auto'
      },
      formBox: {
        background: '#fff',
        padding: mobile ? 20 : 40,
        borderRadius: 14,
        width: '100%',
        maxWidth: 700,
        maxHeight: '90dvh',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        boxShadow: '0 20px 60px rgba(0,0,0,0.2)'
      },
      title: { fontSize: 24, fontWeight: 'bold', marginBottom: 20 },
      grid: { display: 'grid', gap: 20, alignItems: 'start' },
      card: { background: '#f9fafb', padding: 20, borderRadius: 10, boxShadow: '0 2px 6px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: 10 },
      cardTitle: { marginBottom: 10, fontWeight: 'bold' },
      row: { display: 'flex', gap: 10, flexDirection: mobile ? 'column' : 'row' },
      inputBox: { display: 'flex', flexDirection: 'column', gap: 4 },
      input: { height: 45, padding: 10, fontSize: 16, borderRadius: 6, border: '1px solid #334155', width: '100%' },
      termsRow: { marginTop: 16, display: 'flex', alignItems: 'center', gap: 10 },
      termsLabel: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, cursor: 'pointer' },
      panelControlBtn: {
        width: '100%',
        padding: '10px 12px',
        background: '#FFFFFF',
        color: '#1F2937',
        border: '1px solid #E5E7EB',
        borderRadius: 12,
        cursor: 'pointer',
        textAlign: 'center',
        fontSize: 14,
        fontWeight: 500,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        transition: 'all 0.18s ease'
      },
      formButtons: { display: 'flex', gap: 10, marginTop: 20 },
      backBtn: { flex: 1, padding: 12, background: '#374151', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' },
      confirmBtn: { flex: 1, padding: 12, background: '#4f46e5', color: 'white', border: 'none', borderRadius: 6, cursor: 'pointer' },
      moveDot: { position: 'absolute', top: -36, left: -6, width: 42, height: 42, background: '#10b981', borderRadius: '50%', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'grab', touchAction: 'none', fontSize: 20, zIndex: 99999, pointerEvents: 'auto' },
      rotateDot: { position: 'absolute', top: -36, left: '50%', transform: 'translateX(-50%)', width: 42, height: 42, background: '#3b82f6', borderRadius: '50%', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'grab', touchAction: 'none', fontSize: 20, zIndex: 99999, pointerEvents: 'auto' },
      deleteDot: { position: 'absolute', top: -14, right: -14, width: 26, height: 26, background: '#ef4444', borderRadius: '50%', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', zIndex: 99999, pointerEvents: 'auto' },
      resizeDot: { position: 'absolute', right: -8, bottom: -8, width: 42, height: 42, background: '#111827', borderRadius: '50%', cursor: 'nwse-resize', touchAction: 'none', zIndex: 99999, pointerEvents: 'auto' },
      lockDot: { position: 'absolute', left: -14, bottom: -14, width: 26, height: 26, background: '#6d28d9', borderRadius: '50%', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 14, zIndex: 99999, pointerEvents: 'auto' },
      zUpDot: { position: 'absolute', right: -14, top: '50%', transform: 'translateY(-50%)', width: 26, height: 26, background: '#0ea5e9', borderRadius: '50%', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 12, zIndex: 99999, pointerEvents: 'auto' },
      zDownDot: { position: 'absolute', left: -14, top: '50%', transform: 'translateY(-50%)', width: 26, height: 26, background: '#2563eb', borderRadius: '50%', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 12, zIndex: 99999, pointerEvents: 'auto' },
      mobileToolbar: {
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        paddingTop: 12,
        paddingBottom: `calc(12px + ${safeB})`,
        paddingLeft: `max(10px, ${safeL})`,
        paddingRight: `max(10px, ${safeR})`,
        background: '#ffffff',
        borderTop: '1px solid #E5E7EB',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 12,
        zIndex: 9999,
        boxShadow: '0 -4px 20px rgba(0,0,0,0.06)'
      },
      mobileBtn: { minWidth: 120, background: '#7bbcbc', color: 'white', border: 'none', borderRadius: 20, padding: '14px 18px', fontSize: 14, fontWeight: 'bold', cursor: 'pointer', textAlign: 'center' },
      mobileNextBtn: { flex: 1, minWidth: 0, background: '#f59e0b', color: 'white', border: 'none', borderRadius: 18, padding: '12px 10px', fontSize: 14, fontWeight: 'bold', cursor: 'pointer', textAlign: 'center' },
      bottomBar: { height: 10 }
    }
    return baseStyles
  }

  const styles = getStyles(isMobile)
  const zoomPercent = Math.round(((zoom - 0.25) / (1 - 0.25)) * 100)
  const zoomSliderBackground = `linear-gradient(to right, #2563EB 0%, #2563EB ${zoomPercent}%, #E5E7EB ${zoomPercent}%, #E5E7EB 100%)`

  if (paymentSuccess) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          boxSizing: 'border-box',
          paddingTop: 'max(20px, env(safe-area-inset-top, 0px))',
          paddingBottom: 'max(20px, env(safe-area-inset-bottom, 0px))',
          paddingLeft: 'max(20px, env(safe-area-inset-left, 0px))',
          paddingRight: 'max(20px, env(safe-area-inset-right, 0px))',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#D7E9FF',
          animation: 'successFadeIn 0.45s ease'
        }}
      >
        <style>{`
          @keyframes successFadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
        <div
          style={{
            width: '100%',
            maxWidth: 560,
            background: '#EDF5FF',
            borderRadius: 24,
            boxShadow: '0 18px 45px rgba(31,41,55,0.12)',
            padding: '34px 28px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 18
          }}
        >
          <img
            src="/fox-logo.png"
            alt="fox logo"
            style={{
              height: 118,
              objectFit: 'contain',
              transform: 'scale(1.1)',
              filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.15))'
            }}
          />
          <h1 style={{ margin: 0, fontSize: 38, fontWeight: 700, color: '#1F2937' }}>
            Dziekujemy za zamówienie!
          </h1>
          <p style={{ margin: 0, maxWidth: 430, fontSize: 16, lineHeight: 1.6, color: '#4B5563' }}>
            twoj list wkrótce zostanie wysłany
          </p>
          <button
            onClick={() => {
              setPaymentSuccess(false)
              setPage('landing')
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#ea580c'
              e.currentTarget.style.transform = 'translateY(-1px)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f59e0b'
              e.currentTarget.style.transform = 'translateY(0)'
            }}
            style={{
              marginTop: 12,
              padding: '14px 28px',
              border: 'none',
              borderRadius: 999,
              background: '#f59e0b',
              color: '#fff',
              fontWeight: 700,
              fontSize: 16,
              cursor: 'pointer',
              boxShadow: '0 10px 22px rgba(245,158,11,0.35)',
              transition: 'all 0.2s ease'
            }}
          >
            powrót
          </button>
        </div>
      </div>
    )
  }

  if (paymentCanceled) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          boxSizing: 'border-box',
          paddingTop: 'max(20px, env(safe-area-inset-top, 0px))',
          paddingBottom: 'max(20px, env(safe-area-inset-bottom, 0px))',
          paddingLeft: 'max(20px, env(safe-area-inset-left, 0px))',
          paddingRight: 'max(20px, env(safe-area-inset-right, 0px))',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#D7E9FF',
          animation: 'successFadeIn 0.45s ease'
        }}
      >
        <style>{`
          @keyframes successFadeIn {
            from { opacity: 0; transform: translateY(10px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>
        <div
          style={{
            width: '100%',
            maxWidth: 560,
            background: '#EDF5FF',
            borderRadius: 24,
            boxShadow: '0 18px 45px rgba(31,41,55,0.12)',
            padding: '34px 28px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 18
          }}
        >
          <img
            src="/fox-logo.png"
            alt="fox logo"
            style={{
              height: 118,
              objectFit: 'contain',
              transform: 'scale(1.1)',
              filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.15))'
            }}
          />
          <h1 style={{ margin: 0, fontSize: 38, fontWeight: 700, color: '#1F2937' }}>
            Payment canceled
          </h1>
          <p style={{ margin: 0, maxWidth: 430, fontSize: 16, lineHeight: 1.6, color: '#4B5563' }}>
            You can try again anytime
          </p>
          <button
            onClick={() => {
              setPaymentCanceled(false)
              setPage('editor')
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#ea580c'
              e.currentTarget.style.transform = 'translateY(-1px)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f59e0b'
              e.currentTarget.style.transform = 'translateY(0)'
            }}
            style={{
              marginTop: 12,
              padding: '14px 28px',
              border: 'none',
              borderRadius: 999,
              background: '#f59e0b',
              color: '#fff',
              fontWeight: 700,
              fontSize: 16,
              cursor: 'pointer',
              boxShadow: '0 10px 22px rgba(245,158,11,0.35)',
              transition: 'all 0.2s ease'
            }}
          >
            Try again
          </button>
        </div>
      </div>
    )
  }

  if (page === 'landing') {
    return (
      <div style={{
        minHeight: '100dvh',
        boxSizing: 'border-box',
        paddingTop: 'max(20px, env(safe-area-inset-top, 0px))',
        paddingBottom: 'max(20px, env(safe-area-inset-bottom, 0px))',
        paddingLeft: 'max(20px, env(safe-area-inset-left, 0px))',
        paddingRight: 'max(20px, env(safe-area-inset-right, 0px))',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        background: '#D7E9FF',
        color: '#1F2937',
        textAlign: 'center',
        gap: 20
      }}>
        <img
          src="/fox-logo.png"
          alt="fox logo"
          style={{
            height: 120,
            marginBottom: 16,
            transform: 'scale(1.1)',
            filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.15))'
          }}
        />
        <h1 style={{ fontSize: 42, fontWeight: 700, marginBottom: 16 }}>Stwórz swój projekt</h1>
        <p style={{ maxWidth: 500, color: '#4B5563', fontSize: 16 }}>Zaprojektuj własny dokument, dodaj zdjęcia i tekst, a my zajmiemy się resztą.</p>
        <button
          onClick={() => setPage('editor')}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = '#ea580c'
            e.currentTarget.style.transform = 'scale(1.05)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = '#f59e0b'
            e.currentTarget.style.transform = 'scale(1)'
          }}
          style={{ padding: '14px 28px', fontSize: 18, borderRadius: 999, border: 'none', background: '#f59e0b', color: 'white', cursor: 'pointer', fontWeight: 600, transition: 'all 0.2s ease' }}
        >
          Kontynuuj →
        </button>
        <div style={{ display: 'flex', gap: 20, marginTop: 10 }}>
          <button
            onClick={() => setModal('contact')}
            style={{
              padding: '10px 18px',
              borderRadius: 999,
              border: 'none',
              background: '#1F2937',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            Kontakt
          </button>

          <button
            onClick={() => setModal('terms')}
            style={{
              padding: '10px 18px',
              borderRadius: 999,
              border: 'none',
              background: '#374151',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            Regulamin
          </button>
        </div>
        {modal && (
          <div
            onClick={() => setModal(null)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(0,0,0,0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 99999
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'white',
                padding: 30,
                borderRadius: 16,
                maxWidth: 500,
                width: '90%',
                maxHeight: '80vh',
                overflowY: 'auto'
              }}
            >
              <button
                onClick={() => setModal(null)}
                style={{
                  position: 'absolute',
                  right: 20,
                  top: 20,
                  border: 'none',
                  background: 'transparent',
                  fontSize: 20,
                  cursor: 'pointer'
                }}
              >
                ✕
              </button>

              {modal === 'contact' && (
                <>
                  <h2>Kontakt</h2>
                  <p>Email: listydodruku@gmail.com</p>
                  <p>Odpowiadamy w ciągu 24h.</p>
                </>
              )}

              {modal === 'terms' && (
                <>
                  <h2>Regulamin</h2>
                  <p># # REGULAMIN SERWISU

## §1 Informacje podstawowe

1. Właścicielem Serwisu jest osoba fizyczna prowadząca działalność nierejestrowaną (dalej: „Usługodawca”).
2. Kontakt z Usługodawcą możliwy jest pod adresem e-mail: [listydodruku@gmail.com].
3. Serwis umożliwia tworzenie i zamawianie spersonalizowanych dokumentów w formacie PDF.

---

## §2 Zakres usługi

1. Serwis pozwala użytkownikowi na:

   * stworzenie własnego projektu dokumentu,
   * wygenerowanie pliku PDF,
   * złożenie zamówienia po dokonaniu płatności.
2. Dokument tworzony jest na podstawie danych wprowadzonych przez użytkownika.
3. Usługodawca nie ingeruje w treść dokumentu.

---

## §3 Zasady składania zamówienia

1. Zamówienie następuje poprzez:

   * uzupełnienie formularza,
   * wygenerowanie projektu,
   * dokonanie płatności.
2. Warunkiem realizacji jest poprawne podanie danych:

   * adres e-mail,
   * dane nadawcy i odbiorcy.
3. Użytkownik ponosi pełną odpowiedzialność za poprawność wprowadzonych danych.

---

## §4 Płatności

1. Płatności realizowane są za pośrednictwem zewnętrznego operatora płatności (np. Stripe).
2. Cena usługi jest widoczna przed dokonaniem płatności.
3. Brak płatności oznacza brak realizacji zamówienia.

---

## §5 Realizacja zamówienia

1. Realizacja zamówienia rozpoczyna się po zaksięgowaniu płatności.
2. Czas realizacji wynosi zazwyczaj do 24 godzin.
3. W wyjątkowych przypadkach (np. problemy techniczne) czas realizacji może się wydłużyć.

---

## §6 Produkty cyfrowe i brak odstąpienia

1. Zamawiany produkt jest produktem cyfrowym tworzonym na indywidualne zamówienie użytkownika.
2. Zgodnie z art. 38 ustawy o prawach konsumenta, użytkownik traci prawo do odstąpienia od umowy w momencie rozpoczęcia realizacji usługi.
3. Dokonując płatności użytkownik wyraża zgodę na natychmiastowe rozpoczęcie realizacji zamówienia.

---

## §7 Odpowiedzialność

1. Usługodawca nie ponosi odpowiedzialności za:

   * błędnie wprowadzone dane,
   * nieprawidłowe adresy,
   * treści wprowadzone przez użytkownika.
2. Zabronione jest wprowadzanie treści:

   * niezgodnych z prawem,
   * obraźliwych,
   * naruszających prawa osób trzecich.

---

## §8 Reklamacje

1. Reklamacje należy zgłaszać na adres e-mail: [listydodruku@gmail.com].
2. Reklamacja powinna zawierać:

   * opis problemu,
   * dane zamówienia.
3. Reklamacje rozpatrywane są w terminie do 14 dni.

---

## §9 Dane osobowe (RODO)

1. Dane użytkownika przetwarzane są wyłącznie w celu realizacji zamówienia.
2. Dane mogą być przekazywane podmiotom trzecim wyłącznie w zakresie niezbędnym do realizacji usługi (np. operator płatności).
3. Użytkownik ma prawo do:

   * dostępu do danych,
   * poprawiania danych,
   * żądania usunięcia danych.

---

## §10 Postanowienia końcowe

1. Regulamin może ulec zmianie.
2. Aktualna wersja regulaminu dostępna jest w Serwisie.
3. W sprawach nieuregulowanych zastosowanie ma prawo polskie.

</p>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    )
  }

  if (page === 'form') {
    return (
      <div style={styles.formOverlay}>
        <div style={styles.formBox}>
          <h2 style={styles.title}>Dane do wysyłki</h2>
          <div style={styles.inputBox}>
            <label>Email</label>
            <input type="email" style={styles.input} placeholder="Twój email" value={form.email} onChange={(e) => setForm(prev => ({ ...prev, email: e.target.value }))} />
          </div>
          <div style={{ ...styles.grid, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr' }}>
            <div style={styles.card}>
              <div style={styles.cardTitle}>Nadawca</div>
              <div style={styles.row}>
                <div style={styles.inputBox}>
                  <label>Imię i nazwisko</label>
                  <input style={styles.input} value={form.nadawca.imie} onChange={(e) => setForm(prev => ({ ...prev, nadawca: { ...prev.nadawca, imie: e.target.value } }))} />
                </div>
                <div style={styles.inputBox}>
                  <label>Miasto</label>
                  <input style={styles.input} value={form.nadawca.miasto} onChange={(e) => setForm(prev => ({ ...prev, nadawca: { ...prev.nadawca, miasto: e.target.value } }))} />
                </div>
              </div>
              <div style={styles.row}>
                <div style={styles.inputBox}>
                  <label>Kod pocztowy</label>
                  <input style={styles.input} value={form.nadawca.kod} onChange={(e) => setForm(prev => ({ ...prev, nadawca: { ...prev.nadawca, kod: e.target.value } }))} />
                </div>
                <div style={styles.inputBox}>
                  <label>Województwo</label>
                  <input style={styles.input} value={form.nadawca.woj} onChange={(e) => setForm(prev => ({ ...prev, nadawca: { ...prev.nadawca, woj: e.target.value } }))} />
                </div>
              </div>
              <div style={styles.inputBox}>
                <label>Adres</label>
                <input style={styles.input} value={form.nadawca.adres} onChange={(e) => setForm(prev => ({ ...prev, nadawca: { ...prev.nadawca, adres: e.target.value } }))} />
              </div>
            </div>
            <div style={styles.card}>
              <div style={styles.cardTitle}>Odbiorca</div>
              <div style={styles.row}>
                <div style={styles.inputBox}>
                  <label>Imię i nazwisko</label>
                  <input style={styles.input} value={form.odbiorca.imie} onChange={(e) => setForm(prev => ({ ...prev, odbiorca: { ...prev.odbiorca, imie: e.target.value } }))} />
                </div>
                <div style={styles.inputBox}>
                  <label>Miasto</label>
                  <input style={styles.input} value={form.odbiorca.miasto} onChange={(e) => setForm(prev => ({ ...prev, odbiorca: { ...prev.odbiorca, miasto: e.target.value } }))} />
                </div>
              </div>
              <div style={styles.row}>
                <div style={styles.inputBox}>
                  <label>Kod pocztowy</label>
                  <input style={styles.input} value={form.odbiorca.kod} onChange={(e) => setForm(prev => ({ ...prev, odbiorca: { ...prev.odbiorca, kod: e.target.value } }))} />
                </div>
                <div style={styles.inputBox}>
                  <label>Województwo</label>
                  <input style={styles.input} value={form.odbiorca.woj} onChange={(e) => setForm(prev => ({ ...prev, odbiorca: { ...prev.odbiorca, woj: e.target.value } }))} />
                </div>
              </div>
              <div style={styles.inputBox}>
                <label>Adres</label>
                <input style={styles.input} value={form.odbiorca.adres} onChange={(e) => setForm(prev => ({ ...prev, odbiorca: { ...prev.odbiorca, adres: e.target.value } }))} />
              </div>
            </div>
          </div>
          <div style={styles.termsRow}>
            <label style={styles.termsLabel}>
              <input
                type="checkbox"
                checked={form.acceptTerms}
                onChange={(e) => setForm((prev) => ({ ...prev, acceptTerms: e.target.checked }))}
              />
              <span>Akceptuję regulamin</span>
            </label>
          </div>
          <div style={styles.formButtons}>
            <button style={styles.backBtn} onClick={() => setPage('editor')}>⬅️ Wróć</button>
            <button
              onClick={handleGenerateClick}
              disabled={isGenerating}
              style={{
                ...styles.confirmBtn,
                opacity: isGenerating ? 0.6 : 1,
                cursor: isGenerating ? 'not-allowed' : 'pointer'
              }}
            >
              {isGenerating ? `⏳ ${progress}%` : '✔️ Zapłać'}
            </button>
          </div>
        </div>
        <div ref={hiddenRef} style={{position: 'fixed', top: -10000, left: -10000, width: '794px', height: '1123px', background: '#fff'}}>
          {[...elements]
            .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
            .map(el => (
            <div key={el.id} style={{ position: 'absolute', left: el.x, top: el.y, width: el.width, height: el.height, transform: `rotate(${el.rotate}deg)`, zIndex: el.zIndex || 0, background: el.type === 'text' ? 'transparent' : 'none' }}>
              {el.type === 'image' ? (
                <img src={el.src} style={{ width: '100%', height: '100%' }} alt="" />
              ) : (
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    fontSize: el.fontSize,
                    fontFamily: el.fontFamily,
                    color: el.color,
                    fontWeight: el.fontWeight ?? DEFAULT_TEXT_STYLE.fontWeight,
                    fontStyle: el.fontStyle ?? DEFAULT_TEXT_STYLE.fontStyle,
                    textDecoration: el.textDecoration ?? DEFAULT_TEXT_STYLE.textDecoration,
                    textAlign: el.textAlign ?? DEFAULT_TEXT_STYLE.textAlign,
                    lineHeight: el.lineHeight ?? DEFAULT_TEXT_STYLE.lineHeight,
                    letterSpacing: `${el.letterSpacing ?? DEFAULT_TEXT_STYLE.letterSpacing}px`
                  }}
                >
                  {el.text}
                </div>
              )}
            </div>
          ))}
        </div>
        <div ref={hiddenPage2Ref} style={{
          position: 'fixed',
          top: -10000,
          left: -10000,
          width: '794px',
          height: '1123px',
          background: '#fff',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column',
          fontSize: 18
        }}>
          <div style={{ marginBottom: 40 }}>
            <b>NADAWCA:</b><br />
            {form.nadawca.imie}<br />
            {form.nadawca.adres}<br />
            {form.nadawca.kod} {form.nadawca.miasto}<br />
            {form.nadawca.woj}
          </div>

          <div>
            <b>ODBIORCA:</b><br />
            {form.odbiorca.imie}<br />
            {form.odbiorca.adres}<br />
            {form.odbiorca.kod} {form.odbiorca.miasto}<br />
            {form.odbiorca.woj}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={styles.app}>
      <style>{`
        .zoom-slider {
          -webkit-appearance: none;
          appearance: none;
          width: 140px;
          height: 6px;
          border-radius: 999px;
          background: ${zoomSliderBackground};
          transition: background 0.2s ease;
          cursor: pointer;
        }
        .zoom-slider::-webkit-slider-runnable-track {
          height: 6px;
          border-radius: 999px;
          background: transparent;
        }
        .zoom-slider::-moz-range-track {
          height: 6px;
          border-radius: 999px;
          background: transparent;
        }
        .zoom-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #2563EB;
          border: 2px solid #FFFFFF;
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
          transition: transform 0.15s ease, background 0.15s ease;
          margin-top: -4px;
        }
        .zoom-slider::-moz-range-thumb {
          width: 14px;
          height: 14px;
          border-radius: 50%;
          background: #2563EB;
          border: 2px solid #FFFFFF;
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
          transition: transform 0.15s ease, background 0.15s ease;
        }
        .zoom-slider:hover::-webkit-slider-thumb {
          transform: scale(1.15);
        }
        .zoom-slider:hover::-moz-range-thumb {
          transform: scale(1.15);
        }
        .zoom-slider:active::-webkit-slider-thumb {
          transform: scale(1.25);
          background: #1D4ED8;
        }
        .zoom-slider:active::-moz-range-thumb {
          transform: scale(1.25);
          background: #1D4ED8;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div style={styles.topbarOuter}>
        <div style={styles.topbarInner}>
        <div 
  style={{ ...styles.logo, cursor: 'pointer' }}
  onClick={() => setPage('landing')}
>
  <img
    src="/fox-logo.png"
    alt="logo"
              style={{
                height: 40,
                objectFit: 'contain',
                transform: 'scale(1.6)',
                transformOrigin: 'center'
              }}
            />
          </div>
          <div style={styles.menu}>
            <div style={{...styles.menuItem, ...(page === 'editor' ? styles.activeMenuItem : {})}} onClick={() => setPage('editor')}>Strona 1</div>
            <div style={{...styles.menuItem, ...(page === 'form' ? styles.activeMenuItem : {}), cursor: 'default'}}>Wyślij PDF</div>
          </div>
          {page === 'editor' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <button
                type="button"
                title="Cofnij (Ctrl+Z)"
                onClick={undo}
                disabled={pastRef.current.length === 0}
                onMouseEnter={(e) => {
                  if (pastRef.current.length === 0) return
                  e.currentTarget.style.background = '#93C5FD'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#BFDBFE'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                style={{
                  background: '#BFDBFE',
                  border: '1px solid #93C5FD',
                  borderRadius: 10,
                  padding: isMobile ? '8px 10px' : '8px 12px',
                  color: '#1E3A8A',
                  fontWeight: 'bold',
                  fontSize: 16,
                  cursor: pastRef.current.length === 0 ? 'default' : 'pointer',
                  opacity: pastRef.current.length === 0 ? 0.4 : 1,
                  transition: 'all 0.2s ease'
                }}
              >
                ↶
              </button>
              <button
                type="button"
                title="Ponów (Ctrl+Shift+Z)"
                onClick={redo}
                disabled={futureRef.current.length === 0}
                onMouseEnter={(e) => {
                  if (futureRef.current.length === 0) return
                  e.currentTarget.style.background = '#93C5FD'
                  e.currentTarget.style.transform = 'scale(1.05)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#BFDBFE'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
                style={{
                  background: '#BFDBFE',
                  border: '1px solid #93C5FD',
                  borderRadius: 10,
                  padding: isMobile ? '8px 10px' : '8px 12px',
                  color: '#1E3A8A',
                  fontWeight: 'bold',
                  fontSize: 16,
                  cursor: futureRef.current.length === 0 ? 'default' : 'pointer',
                  opacity: futureRef.current.length === 0 ? 0.4 : 1,
                  transition: 'all 0.2s ease'
                }}
              >
                ↷
              </button>
            </div>
          )}
          {page === 'editor' && !isMobile && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ color: '#1F2937', fontSize: 13, fontWeight: 500, minWidth: 80 }}>Zoom: {Math.round(zoom * 100)}%</span>
              <input
                className="zoom-slider"
                type="range"
                min={0.25}
                max={1}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
              />
            </div>
          )}
          <button style={styles.nextButton} onClick={() => setPage('form')}>Dalej →</button>
        </div>
      </div>
      <div style={styles.main}>
        {!isMobile && (
          <div style={styles.sidebar}>
            <div
              style={styles.sidebarBtn}
              onClick={() => fileRef.current?.click()}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#E6F0FF'
                e.currentTarget.style.transform = 'translateY(-2px)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#F1F6FF'
                e.currentTarget.style.transform = 'translateY(0)'
              }}
              onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.98)')}
              onMouseUp={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
            >
              <span style={styles.sidebarBtnIcon}>＋</span>
              <span>Dodaj zdjęcie</span>
            </div>
            <div
              style={styles.sidebarBtn}
              onClick={addText}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#E6F0FF'
                e.currentTarget.style.transform = 'translateY(-2px)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#F1F6FF'
                e.currentTarget.style.transform = 'translateY(0)'
              }}
              onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.98)')}
              onMouseUp={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
            >
              <span style={styles.sidebarBtnIcon}>T</span>
              <span>Dodaj tekst</span>
            </div>
          </div>
        )}
        <div ref={wrapperRef} style={{ ...styles.canvasWrapper, padding: isMobile ? 10 : 40, alignItems: 'flex-start' }}>
          <div 
            style={{
              width: A4_WIDTH * zoom,
              height: A4_HEIGHT * zoom,
              boxShadow: '0 20px 50px rgba(0,0,0,0.15)',
              margin: 0,
              transition: 'all 0.1s ease-out',
              background: '#fff',
              borderRadius: 8,
              border: '2px dashed #333',
              position: 'relative',
              overflow: 'visible',
              touchAction: 'none',
              ...(isMobile ? { maxWidth: '100vw', boxSizing: 'border-box' } : {})
            }} 
            ref={canvasRef}
            onMouseDown={(e) => { if (e.target === e.currentTarget) setSelectedId(null) }}
            onTouchStart={(e) => { if (e.target === e.currentTarget) setSelectedId(null) }}
          >
            {elements.map((el) => {
              const isSelected = el.id === selectedId
              return (
              <Rnd key={el.id} size={{ width: el.width * zoom, height: el.height * zoom }} position={{ x: el.x * zoom, y: el.y * zoom }} bounds={{left: 0, top: 0, right: A4_WIDTH * zoom - el.width * zoom, bottom: A4_HEIGHT * zoom - el.height * zoom}} enableResizing={false} disableDragging={true} onMouseDown={() => {setSelectedId(el.id)}} onTouchStart={() => {setSelectedId(el.id)}} style={{ zIndex: isSelected ? 9999 : el.zIndex }}>
                <div data-editor-element={el.id} style={{width: '100%', height: '100%', position: 'relative', transform: `rotate(${el.rotate}deg)`, border: el.id === selectedId ? '1px solid #4f46e5' : 'none'}}>
                  {el.type === 'image' && (<img src={el.src} style={{ width: '100%', height: '100%' }} alt="" />)}
                  {el.type === 'text' && (
                    <textarea
                      value={el.text}
                      readOnly={el.locked}
                      onClick={() => setSelectedId(el.id)}
                      onTouchStart={() => setSelectedId(el.id)}
                      onChange={(e) => update(el.id, { text: e.target.value })}
                      style={{
                        width: '100%',
                        height: '100%',
                        boxSizing: 'border-box',
                        fontSize: el.fontSize * zoom,
                        color: el.color,
                        fontFamily: el.fontFamily || FONT_OPTIONS[0],
                        ...getTextTypographyStyles(el, zoom),
                        border: 'none',
                        outline: 'none',
                        resize: 'none',
                        background: 'transparent',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word'
                      }}
                    />
                  )}
                  {selectedId === el.id && (
                    <>
                      {isMobile && el.type === 'text' && !showTextPanel && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation()
                            setShowTextPanel(true)
                          }}
                          onTouchStart={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setShowTextPanel(true)
                          }}
                          style={{
                            position: 'absolute',
                            top: -50,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            padding: '6px 12px',
                            borderRadius: 999,
                            background: '#4f46e5',
                            color: '#fff',
                            border: 'none',
                            fontSize: 13,
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            zIndex: 100000,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          ✏️ Edytuj
                        </button>
                      )}
                      {!el.locked && <div onMouseDown={(e) => startMove(e, el)} onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); startMove(e, el) }} style={styles.moveDot}>⠿</div>}
                      {!el.locked && <div onMouseDown={(e) => startRotate(e, el)} onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); startRotate(e, el) }} style={styles.rotateDot}>↻</div>}
                      <div onClick={() => deleteEl(el.id)} onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); deleteEl(el.id) }} style={styles.deleteDot}>✕</div>
                      <div
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleLock(el.id)
                        }}
                        onTouchStart={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          toggleLock(el.id)
                        }}
                        style={styles.lockDot}
                      >
                        {el.locked ? '🔒' : '🔓'}
                      </div>
                      <div
                        onClick={(e) => {
                          e.stopPropagation()
                          bringToFront(el.id)
                        }}
                        onTouchStart={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          bringToFront(el.id)
                        }}
                        style={styles.zUpDot}
                      >
                        ⬆️
                      </div>
                      <div
                        onClick={(e) => {
                          e.stopPropagation()
                          sendToBack(el.id)
                        }}
                        onTouchStart={(e) => {
                          e.preventDefault()
                          e.stopPropagation()
                          sendToBack(el.id)
                        }}
                        style={styles.zDownDot}
                      >
                        ⬇️
                      </div>
                      {!el.locked && <div onMouseDown={(e) => startResize(e, el)} onTouchStart={(e) => { e.preventDefault(); e.stopPropagation(); startResize(e, el) }} style={styles.resizeDot} />}
                    </>
                  )}
                </div>
              </Rnd>
              )
            })}
          </div>
        </div>
        {!isMobile && (
          <div style={styles.rightPanel} data-editor-panel>
            {!selected && 'Wybierz element do edycji'}
            {selected && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                <button
                  type="button"
                  onClick={() => toggleLock(selected.id)}
                  onMouseEnter={(e) => {
                    if (!selected.locked) {
                      e.currentTarget.style.background = '#F1F6FF'
                      e.currentTarget.style.borderColor = '#BFDBFE'
                    }
                    e.currentTarget.style.transform = 'translateY(-1px)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = selected.locked ? '#FEF3C7' : '#E0F2FE'
                    e.currentTarget.style.color = selected.locked ? '#92400E' : '#1E40AF'
                    e.currentTarget.style.borderColor = '#E5E7EB'
                    e.currentTarget.style.transform = 'translateY(0)'
                  }}
                  onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.98)')}
                  onMouseUp={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
                  style={{
                    ...styles.panelControlBtn,
                    background: selected.locked ? '#FEF3C7' : '#E0F2FE',
                    color: selected.locked ? '#92400E' : '#1E40AF'
                  }}
                >
                  {selected.locked ? '🔒 Zablokowany' : '🔓 Odblokowany'}
                </button>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <button
                    type="button"
                    onClick={() => bringToFront(selected.id)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#F1F6FF'
                      e.currentTarget.style.borderColor = '#BFDBFE'
                      e.currentTarget.style.transform = 'translateY(-1px)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#FFFFFF'
                      e.currentTarget.style.borderColor = '#E5E7EB'
                      e.currentTarget.style.transform = 'translateY(0)'
                    }}
                    onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.98)')}
                    onMouseUp={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
                    style={styles.panelControlBtn}
                  >
                    <span style={{ fontSize: 13 }}>⬆️</span> Na wierzch
                  </button>
                  <button
                    type="button"
                    onClick={() => sendToBack(selected.id)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#F1F6FF'
                      e.currentTarget.style.borderColor = '#BFDBFE'
                      e.currentTarget.style.transform = 'translateY(-1px)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#FFFFFF'
                      e.currentTarget.style.borderColor = '#E5E7EB'
                      e.currentTarget.style.transform = 'translateY(0)'
                    }}
                    onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.98)')}
                    onMouseUp={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
                    style={styles.panelControlBtn}
                  >
                    <span style={{ fontSize: 13 }}>⬇️</span> Na spód
                  </button>
                  <button
                    type="button"
                    onClick={() => bringForward(selected.id)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#F1F6FF'
                      e.currentTarget.style.borderColor = '#BFDBFE'
                      e.currentTarget.style.transform = 'translateY(-1px)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#FFFFFF'
                      e.currentTarget.style.borderColor = '#E5E7EB'
                      e.currentTarget.style.transform = 'translateY(0)'
                    }}
                    onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.98)')}
                    onMouseUp={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
                    style={styles.panelControlBtn}
                  >
                    <span style={{ fontSize: 13 }}>🔼</span> Wyżej
                  </button>
                  <button
                    type="button"
                    onClick={() => sendBackward(selected.id)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#F1F6FF'
                      e.currentTarget.style.borderColor = '#BFDBFE'
                      e.currentTarget.style.transform = 'translateY(-1px)'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = '#FFFFFF'
                      e.currentTarget.style.borderColor = '#E5E7EB'
                      e.currentTarget.style.transform = 'translateY(0)'
                    }}
                    onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.98)')}
                    onMouseUp={(e) => (e.currentTarget.style.transform = 'translateY(-1px)')}
                    style={styles.panelControlBtn}
                  >
                    <span style={{ fontSize: 13 }}>🔽</span> Niżej
                  </button>
                </div>
              </div>
            )}
            {selected && selected.type === 'text' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                <h3 style={{ marginBottom: 10 }}>Tekst</h3>
                <div style={styles.inputBox}>
                  <label>Czcionka</label>
                  <select value={selected.fontFamily || FONT_OPTIONS[0]} onChange={(e) => update(selected.id, { fontFamily: e.target.value })} style={styles.input}>
                    {FONT_OPTIONS.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => update(selected.id, { fontWeight: (selected.fontWeight ?? DEFAULT_TEXT_STYLE.fontWeight) === 'bold' ? 'normal' : 'bold' })}
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: '1px solid #334155',
                      borderRadius: 6,
                      background: (selected.fontWeight ?? DEFAULT_TEXT_STYLE.fontWeight) === 'bold' ? '#4f46e5' : '#fff',
                      color: (selected.fontWeight ?? DEFAULT_TEXT_STYLE.fontWeight) === 'bold' ? '#fff' : '#111',
                      fontWeight: 'bold',
                      cursor: 'pointer'
                    }}
                  >
                    B
                  </button>
                  <button
                    type="button"
                    onClick={() => update(selected.id, { fontStyle: (selected.fontStyle ?? DEFAULT_TEXT_STYLE.fontStyle) === 'italic' ? 'normal' : 'italic' })}
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: '1px solid #334155',
                      borderRadius: 6,
                      background: (selected.fontStyle ?? DEFAULT_TEXT_STYLE.fontStyle) === 'italic' ? '#4f46e5' : '#fff',
                      color: (selected.fontStyle ?? DEFAULT_TEXT_STYLE.fontStyle) === 'italic' ? '#fff' : '#111',
                      fontStyle: 'italic',
                      cursor: 'pointer'
                    }}
                  >
                    I
                  </button>
                  <button
                    type="button"
                    onClick={() => update(selected.id, { textDecoration: (selected.textDecoration ?? DEFAULT_TEXT_STYLE.textDecoration) === 'underline' ? 'none' : 'underline' })}
                    style={{
                      flex: 1,
                      padding: '8px',
                      border: '1px solid #334155',
                      borderRadius: 6,
                      background: (selected.textDecoration ?? DEFAULT_TEXT_STYLE.textDecoration) === 'underline' ? '#4f46e5' : '#fff',
                      color: (selected.textDecoration ?? DEFAULT_TEXT_STYLE.textDecoration) === 'underline' ? '#fff' : '#111',
                      textDecoration: 'underline',
                      cursor: 'pointer'
                    }}
                  >
                    U
                  </button>
                </div>
                <div style={styles.inputBox}>
                  <label>Wyrównanie</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {(['left', 'center', 'right']).map((align) => (
                      <button
                        key={align}
                        type="button"
                        onClick={() => update(selected.id, { textAlign: align })}
                        style={{
                          flex: 1,
                          padding: '8px',
                          border: '1px solid #334155',
                          borderRadius: 6,
                          background: (selected.textAlign ?? DEFAULT_TEXT_STYLE.textAlign) === align ? '#4f46e5' : '#fff',
                          color: (selected.textAlign ?? DEFAULT_TEXT_STYLE.textAlign) === align ? '#fff' : '#111',
                          cursor: 'pointer',
                          fontSize: 14
                        }}
                      >
                        {align === 'left' ? 'L' : align === 'center' ? 'C' : 'R'}
                      </button>
                    ))}
                  </div>
                </div>
                <div style={styles.inputBox}>
                  <label>Interlinia: {(selected.lineHeight ?? DEFAULT_TEXT_STYLE.lineHeight).toFixed(2)}</label>
                  <input type="range" min={1} max={3} step={0.05} value={selected.lineHeight ?? DEFAULT_TEXT_STYLE.lineHeight} onChange={(e) => update(selected.id, { lineHeight: parseFloat(e.target.value) })} style={{ width: '100%' }} />
                </div>
                <div style={styles.inputBox}>
                  <label>Odstępy liter: {(selected.letterSpacing ?? DEFAULT_TEXT_STYLE.letterSpacing).toFixed(1)} px</label>
                  <input type="range" min={0} max={5} step={0.1} value={selected.letterSpacing ?? DEFAULT_TEXT_STYLE.letterSpacing} onChange={(e) => update(selected.id, { letterSpacing: parseFloat(e.target.value) })} style={{ width: '100%' }} />
                </div>
                <div style={styles.inputBox}>
                  <label>Kolor</label>
                  <input type="color" value={selected.color} onChange={(e) => update(selected.id, { color: e.target.value })} style={{ height: 45, border: 'none' }} />
                </div>
                <div style={styles.inputBox}>
                  <label>Rozmiar czcionki: {selected.fontSize}px</label>
                  <input type="range" min={8} max={72} value={selected.fontSize} onChange={(e) => update(selected.id, { fontSize: parseInt(e.target.value) })} style={{ width: '100%' }} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <div style={styles.bottomBar}></div>
      {isMobile && selected && selected.type === 'text' && showTextPanel && (
        <div data-editor-panel style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff', padding: '20px 20px calc(20px + env(safe-area-inset-bottom, 0px))', paddingLeft: 'max(20px, env(safe-area-inset-left, 0px))', paddingRight: 'max(20px, env(safe-area-inset-right, 0px))', borderTopLeftRadius: 20, borderTopRightRadius: 20, boxShadow: '0 -4px 20px rgba(0,0,0,0.1)', zIndex: 10000, maxHeight: '50vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', overscrollBehavior: 'contain' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h3 style={{ margin: 0 }}>Edytuj tekst</h3>
            <button onClick={() => setShowTextPanel(false)} style={{background: '#f3f4f6', border: 'none', borderRadius: '50%', width: 36, height: 36, cursor: 'pointer', fontSize: 20, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>✕</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <button type="button" onClick={() => toggleLock(selected.id)} style={{ ...styles.mobileBtn, padding: '10px' }}>{selected.locked ? '🔒' : '🔓'}</button>
              <button type="button" onClick={() => bringToFront(selected.id)} style={{ ...styles.mobileBtn, padding: '10px' }}>⬆️</button>
              <button type="button" onClick={() => sendToBack(selected.id)} style={{ ...styles.mobileBtn, padding: '10px' }}>⬇️</button>
              <button type="button" onClick={() => bringForward(selected.id)} style={{ ...styles.mobileBtn, padding: '10px' }}>🔼</button>
              <button type="button" onClick={() => sendBackward(selected.id)} style={{ ...styles.mobileBtn, padding: '10px' }}>🔽</button>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Czcionka</label>
              <select value={selected.fontFamily || FONT_OPTIONS[0]} onChange={(e) => update(selected.id, { fontFamily: e.target.value })} style={{ ...styles.input, cursor: 'pointer' }}>
                {FONT_OPTIONS.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={() => update(selected.id, { fontWeight: (selected.fontWeight ?? DEFAULT_TEXT_STYLE.fontWeight) === 'bold' ? 'normal' : 'bold' })}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: '1px solid #334155',
                  borderRadius: 8,
                  background: (selected.fontWeight ?? DEFAULT_TEXT_STYLE.fontWeight) === 'bold' ? '#4f46e5' : '#f3f4f6',
                  color: (selected.fontWeight ?? DEFAULT_TEXT_STYLE.fontWeight) === 'bold' ? '#fff' : '#111',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                B
              </button>
              <button
                type="button"
                onClick={() => update(selected.id, { fontStyle: (selected.fontStyle ?? DEFAULT_TEXT_STYLE.fontStyle) === 'italic' ? 'normal' : 'italic' })}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: '1px solid #334155',
                  borderRadius: 8,
                  background: (selected.fontStyle ?? DEFAULT_TEXT_STYLE.fontStyle) === 'italic' ? '#4f46e5' : '#f3f4f6',
                  color: (selected.fontStyle ?? DEFAULT_TEXT_STYLE.fontStyle) === 'italic' ? '#fff' : '#111',
                  fontStyle: 'italic',
                  cursor: 'pointer'
                }}
              >
                I
              </button>
              <button
                type="button"
                onClick={() => update(selected.id, { textDecoration: (selected.textDecoration ?? DEFAULT_TEXT_STYLE.textDecoration) === 'underline' ? 'none' : 'underline' })}
                style={{
                  flex: 1,
                  padding: '10px',
                  border: '1px solid #334155',
                  borderRadius: 8,
                  background: (selected.textDecoration ?? DEFAULT_TEXT_STYLE.textDecoration) === 'underline' ? '#4f46e5' : '#f3f4f6',
                  color: (selected.textDecoration ?? DEFAULT_TEXT_STYLE.textDecoration) === 'underline' ? '#fff' : '#111',
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
              >
                U
              </button>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Wyrównanie</label>
              <div style={{ display: 'flex', gap: 8 }}>
                {(['left', 'center', 'right']).map((align) => (
                  <button
                    key={align}
                    type="button"
                    onClick={() => update(selected.id, { textAlign: align })}
                    style={{
                      flex: 1,
                      padding: '10px',
                      border: '1px solid #334155',
                      borderRadius: 8,
                      background: (selected.textAlign ?? DEFAULT_TEXT_STYLE.textAlign) === align ? '#4f46e5' : '#f3f4f6',
                      color: (selected.textAlign ?? DEFAULT_TEXT_STYLE.textAlign) === align ? '#fff' : '#111',
                      cursor: 'pointer',
                      fontSize: 16
                    }}
                  >
                    {align === 'left' ? 'L' : align === 'center' ? 'C' : 'R'}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Interlinia: {(selected.lineHeight ?? DEFAULT_TEXT_STYLE.lineHeight).toFixed(2)}</label>
              <input type="range" min={1} max={3} step={0.05} value={selected.lineHeight ?? DEFAULT_TEXT_STYLE.lineHeight} onChange={(e) => update(selected.id, { lineHeight: parseFloat(e.target.value) })} style={{ width: '100%', cursor: 'pointer' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Odstępy liter: {(selected.letterSpacing ?? DEFAULT_TEXT_STYLE.letterSpacing).toFixed(1)} px</label>
              <input type="range" min={0} max={5} step={0.1} value={selected.letterSpacing ?? DEFAULT_TEXT_STYLE.letterSpacing} onChange={(e) => update(selected.id, { letterSpacing: parseFloat(e.target.value) })} style={{ width: '100%', cursor: 'pointer' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Rozmiar tekstu: {selected.fontSize}px</label>
              <input type="range" min={8} max={72} value={selected.fontSize} onChange={(e) => update(selected.id, { fontSize: parseInt(e.target.value) })} style={{ width: '100%', cursor: 'pointer' }} />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Kolor tekstu</label>
              <input type="color" value={selected.color} onChange={(e) => update(selected.id, { color: e.target.value })} style={{ height: 50, width: '100%', borderRadius: 8, border: 'none', cursor: 'pointer' }} />
            </div>
          </div>
        </div>
      )}
      {isMobile && (
        <div style={styles.mobileToolbar}>
          <button style={styles.mobileBtn} onClick={() => fileRef.current?.click()}>📷 Zdjęcie</button>
          <button style={styles.mobileBtn} onClick={addText}>✍️ Tekst</button>
        </div>
      )}
      {isGenerating && (
        <div style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999
        }}>
          <div style={{
            background: 'white',
            padding: 30,
            borderRadius: 16,
            textAlign: 'center',
            width: 280
          }}>
            <div style={{
              width: 40,
              height: 40,
              border: '4px solid #eee',
              borderTop: '4px solid #4f46e5',
              borderRadius: '50%',
              margin: '0 auto 20px',
              animation: 'spin 1s linear infinite'
            }} />

            <div style={{ fontWeight: 'bold', marginBottom: 10 }}>
              Tworzenie listu...
            </div>

            <div style={{
              height: 8,
              background: '#eee',
              borderRadius: 999,
              overflow: 'hidden',
              marginBottom: 10
            }}>
              <div style={{
                width: `${progress}%`,
                height: '100%',
                background: '#4f46e5',
                transition: 'width 0.3s ease'
              }} />
            </div>

            <div style={{ fontSize: 12, color: '#666' }}>
              {progress}%
            </div>
          </div>
        </div>
      )}
      <input type="file" ref={fileRef} accept="image/*" style={{ display: 'none' }} onChange={addImage} />
    </div>
  )
}
